import { MediaLink } from '../../types.ts';
import {
  fetchWithTimeout,
  parseErrorResponse,
  OfflineQueue,
  mediaToServer,
  type ZelfMelden,
} from './base.ts';

export const mediaApi = {
  getMediaList: async (projectId: string): Promise<any[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/media`);
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      return rows.map((row: any) => ({
        id: row.id,
        name: row.filename,
        // #883: filename + filePath werden niet doorgegeven; GEDCOM OBJE-export
        // produceerde lege records (FILE/FORM/TITL leeg). Nu wel beschikbaar.
        filename: row.filename,
        filePath: row.file_path || '',
        mimeType: row.mime_type || '',
        // #15: file_size is a Postgres bigint -> node-pg returns it as a STRING.
        // Cast to Number so consumers (e.g. the gallery total) add instead of
        // string-concatenating (which produced "3.66e+62 MB").
        file_size: Number(row.file_size) || 0,
        created_at: row.created_at || '',
        linkedPersonId: row.linked_person_id,
        linkedPersonIds: Array.isArray(row.linked_person_ids) ? row.linked_person_ids : [],
        // #1597 — alle koppelingen mét rol (portret, huwelijksfoto…); linkedPersonIds blijft.
        links: Array.isArray(row.links)
          ? row.links.map((l: any) => ({
              type: l.entity_type,
              id: l.entity_id,
              role: l.role ?? null,
            }))
          : [],
        caption: row.caption,
        folderId: row.folder_id,
        // #1300 — de rapportbouwer zet een aktescan in het rapport en vermeldt de
        // herkomst eronder. Zonder `sourceId` is die vermelding niet te maken;
        // zonder afmetingen kan de bouwer geen ruimte reserveren vóór het beeld
        // binnen is, en verspringt de paginering onder de handen van de gebruiker.
        sourceId: row.source_id || null,
        width: row.width ?? null,
        height: row.height ?? null,
      }));
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty media list:', error);
      return [];
    }
  },

  /**
   * De rúwe bytes van een media-bestand (#1300).
   *
   * `getMediaList` geeft metagegevens; dit geeft het bestand zelf. De
   * rapportbouwer heeft de bytes nodig om ze te verkleinen en als data-URI in te
   * sluiten — de PDF-renderer en de Word-omzetter hebben geen sessie en kunnen
   * `/api/media/:id/file` niet zelf ophalen.
   */
  getMediaFile: async (mediaId: string): Promise<Blob> => {
    const res = await fetchWithTimeout(`/api/media/${encodeURIComponent(mediaId)}/file`);
    if (!res.ok) await parseErrorResponse(res);
    return res.blob();
  },

  uploadMedia: async (params: {
    name: string;
    data: string;
    mimeType: string;
    projectId: string;
    folderId?: string | null;
    sourceId?: string | null;
  }): Promise<{ id: string; file_path: string; mime_type: string }> => {
    const serverPayload = mediaToServer(params);
    try {
      const res = await fetchWithTimeout(`/api/projects/${params.projectId}/media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serverPayload),
      });
      if (!res.ok) await parseErrorResponse(res);
      return res.json();
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'CREATE',
        endpoint: `/api/projects/${params.projectId}/media`,
        method: 'POST',
        data: serverPayload,
      });
      return { id: '', file_path: '', mime_type: '' };
    }
  },

  deleteMedia: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/media/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/media/${id}`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  // Media folders
  getMediaFolders: async (projectId: string): Promise<any[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/media/folders`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  createMediaFolder: async (projectId: string, name: string): Promise<any> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/media/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  renameMediaFolder: async (folderId: string, name: string): Promise<any> => {
    const res = await fetchWithTimeout(`/api/media/folders/${folderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deleteMediaFolder: async (folderId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/media/folders/${folderId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  updateMedia: async (
    mediaId: string,
    updates: {
      folder_id?: string | null;
      linked_person_id?: string | null;
      source_id?: string | null;
      caption?: string | null;
    }
  ): Promise<any> => {
    const res = await fetchWithTimeout(`/api/media/${mediaId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  batchMoveMedia: async (ids: string[], folderId: string | null): Promise<void> => {
    const res = await fetchWithTimeout('/api/media/batch-move', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, folder_id: folderId }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  batchDeleteMedia: async (ids: string[]): Promise<{ deleted: number }> => {
    const res = await fetchWithTimeout('/api/media/batch-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // --- Media links (OBJE many-to-many) ---

  getMediaLinks: async (mediaId: string): Promise<MediaLink[]> => {
    const res = await fetchWithTimeout(`/api/media/${mediaId}/links`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  linkMedia: async (
    mediaId: string,
    entityType: string,
    entityId: string,
    role?: string,
    opts?: ZelfMelden
  ): Promise<MediaLink> => {
    const res = await fetchWithTimeout(`/api/media/${mediaId}/links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityType, entityId, role }),
    });
    if (!res.ok) await parseErrorResponse(res, opts);
    return res.json();
  },

  unlinkMedia: async (linkId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/media-links/${linkId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },
};
